import { Link } from "react-router-dom";
import { getAboutMe } from "../../ApiService/admin/AboutMe";
import useApi from "../../hooks/useApi";
import { adminAvatarFallback, imageUrl } from "../../utils/image";
import PageHeader from "./PageHeader";
import { RESOURCES } from "./resources";
import { useAdminLang } from "./i18n";

const CARD_COLORS = ["primary", "secondary", "success", "info", "warning"];

interface StatCardProps {
  title: string;
  icon: string;
  path: string;
  color: string;
  load: () => Promise<unknown[]>;
}

const StatCard = ({ title, icon, path, color, load }: StatCardProps) => {
  const { data, loading, error } = useApi(load, [] as unknown[], [path]);
  const { t } = useAdminLang();

  return (
    <div className="col-xxl col-lg-4 col-sm-6">
      <Link to={`/admin/${path}`} className="card custom-card admin-stat-card">
        <div className="card-body d-flex align-items-center gap-3">
          <span className={`avatar avatar-lg avatar-rounded bg-${color}-transparent`}>
            <i className={`${icon} fs-24`}></i>
          </span>
          <div>
            <span className="d-block text-muted mb-1">{t(title)}</span>
            <h4 className="fw-semibold mb-0">
              {loading ? <span className="spinner-border spinner-border-sm"></span> : error ? "—" : data.length}
            </h4>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default function Dashboard() {
  const about = useApi(getAboutMe, null);
  const resources = Object.values(RESOURCES);
  const { t } = useAdminLang();

  return (
    <>
      <PageHeader title="داشبورد" />

      <div className="row">
        {resources.map((r, index) => (
          <StatCard key={r.path} title={r.title} icon={r.icon} path={r.path} color={CARD_COLORS[index % CARD_COLORS.length]} load={r.list as () => Promise<unknown[]>} />
        ))}
      </div>

      <div className="row">
        <div className="col-xl-5">
          <div className="card custom-card">
            <div className="card-header justify-content-between">
              <div className="card-title">{t("درباره من")}</div>
              <Link to="/admin/about" className="btn btn-sm btn-primary-light">{t("ویرایش")}</Link>
            </div>
            <div className="card-body">
              {about.loading ? (
                <div className="text-center p-4"><div className="spinner-border text-primary"></div></div>
              ) : about.error || !about.data ? (
                <p className="text-muted mb-0">{t("اطلاعات «درباره من» از سرور دریافت نشد.")}</p>
              ) : (
                <>
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <span className="avatar avatar-xl avatar-rounded">
                      <img src={imageUrl(about.data.avatarName, adminAvatarFallback(about.data.name, 96))} alt={about.data.name} />
                    </span>
                    <div>
                      <h5 className="fw-semibold mb-1">{about.data.name}</h5>
                      <span className="text-muted">{about.data.workEx} {t("سال تجربه کاری")}</span>
                    </div>
                  </div>
                  <p className="text-muted admin-clamp-3">{about.data.description}</p>
                  <ul className="list-unstyled mb-0 admin-info-list">
                    <li><i className="ri-mail-line me-2 text-primary"></i><span dir="ltr">{about.data.email}</span></li>
                    <li><i className="ri-phone-line me-2 text-primary"></i><span dir="ltr">{about.data.phone}</span></li>
                    <li><i className="ri-map-pin-line me-2 text-primary"></i>{about.data.residence}</li>
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="col-xl-7">
          <div className="card custom-card">
            <div className="card-header">
              <div className="card-title">{t("دسترسی سریع")}</div>
            </div>
            <div className="card-body">
              <div className="row g-3">
                {resources
                  .filter((r) => r.create)
                  .map((r) => (
                    <div className="col-sm-6" key={r.path}>
                      <Link to={`/admin/${r.path}/add`} className="admin-quick-link">
                        <i className={`${r.icon} fs-20 text-primary`}></i>
                        <span>{t("افزودن")} {t(r.singular)}</span>
                        <i className="ri-arrow-left-s-line ms-auto admin-dir-arrow"></i>
                      </Link>
                    </div>
                  ))}
                <div className="col-sm-6">
                  <a href="/" target="_blank" rel="noopener noreferrer" className="admin-quick-link">
                    <i className="ri-global-line fs-20 text-primary"></i>
                    <span>{t("مشاهده سایت")}</span>
                    <i className="ri-arrow-left-s-line ms-auto admin-dir-arrow"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
